import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowSquareOut, BellRinging } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { InboxThread, ticketHref } from "./InboxThread";
import { activeChat } from "./ChatPanel";

export const POPUP_TYPES = ["message", "task", "reply", "task_update"];
export const announceMessage = (n) => window.dispatchEvent(new CustomEvent("incoming-message", { detail: n }));

export function IncomingMessageHost() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const [msg, setMsg] = useState(null);
  const current = queue[0];

  useEffect(() => {
    const onMsg = (e) => setQueue((q) => (q.some((n) => n.message_id === e.detail.message_id) ? q : [...q, e.detail]));
    window.addEventListener("incoming-message", onMsg);
    return () => window.removeEventListener("incoming-message", onMsg);
  }, []);

  useEffect(() => {
    setMsg(null);
    if (!current) return;
    let alive = true;
    const pull = (first) => api.get(`/inbox/messages/${current.message_id}/live`)
      .then((r) => {
        if (!alive) return;
        const other = r.data.from_id === user?.id ? r.data.to_id : r.data.from_id;
        if (first && activeChat.personId === other) { setQueue((q) => q.slice(1)); return; }
        setMsg(r.data);
      })
      .catch(() => { if (first && alive) setQueue((q) => q.slice(1)); });
    pull(true);
    const t = setInterval(() => pull(false), 2000);
    return () => { alive = false; clearInterval(t); };
  }, [current]);

  if (!current || !msg) return null;
  const otherId = user?.id === msg.from_id ? msg.to_id : msg.from_id;
  const otherName = user?.id === msg.from_id ? msg.to_name : msg.from_name;
  const typedAt = (msg.typing || {})[otherId];
  const typing = typedAt && msg.server_now && new Date(msg.server_now) - new Date(typedAt) < 6000;
  const next = () => setQueue((q) => q.slice(1));
  const refresh = (updated) => {
    if (updated?.id) setMsg(updated);
    else api.get(`/inbox/messages/${msg.id}/live`).then((r) => setMsg(r.data)).catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-[9600] bg-slate-950/45 backdrop-blur-sm flex items-center justify-center p-4" data-testid="incoming-message-modal">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden rh-fade-in">
        <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between gap-3 bg-gradient-to-r from-[#EFF6FF] to-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-[#1E5FEB] flex items-center justify-center shrink-0"><BellRinging size={18} weight="fill" className="text-white" /></span>
            <div className="min-w-0">
              <div className="text-[15px] font-bold text-slate-900 truncate" data-testid="incoming-message-title">{current.title}</div>
              <div className="text-[11.5px] text-slate-500">{queue.length > 1 ? `${queue.length - 1} more waiting` : "Reply below or open the ticket"}</div>
            </div>
          </div>
          <button onClick={next} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white" data-testid="incoming-message-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          <InboxThread key={msg.id} m={msg} me={user?.id} onChange={refresh} initialOpen typingName={typing ? otherName?.split(" ")[0] : null} />
        </div>
        <div className="flex gap-2 p-4 border-t border-[#E5E7EB] bg-[#FAFBFC]">
          {msg.ticket_id && (
            <button onClick={() => { next(); nav(ticketHref(msg.ticket_id, { task: msg.kind === "task" ? msg.id : null })); }} data-testid="incoming-message-open-ticket"
              className="h-10 px-4 rounded-xl border border-[#1E5FEB] bg-white hover:bg-[#EFF6FF] text-[#1E5FEB] text-[13px] font-bold inline-flex items-center gap-1.5 transition-colors">
              <ArrowSquareOut size={14} weight="bold" /> Open {msg.ticket_number}
            </button>
          )}
          <button onClick={next} data-testid="incoming-message-later"
            className="ml-auto h-10 px-5 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[13px] font-bold transition-colors">
            {queue.length > 1 ? "Next" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
